import gql from 'graphql-tag';
import { PayoutSettingsFragment } from './fragments';

export const S3_UPLOAD_REQUEST = gql`
    mutation S3UploadMutation($contentType: String) {
        s3UploadAuthorization(contentType: $contentType) {
            node {
                uploadUrl
                meta {
                    id
                }
            }
        }
    }
`;

export const S3_PAYOUT_UPLOAD_REQUEST = gql`
    mutation S3PayoutUploadMutation {
        s3UploadPayoutInvoice {
            node {
                uploadUrl
                meta {
                    id
                }
            }
        }
    }
`;

/* =================================================
        ORGANIZATIONS
   ================================================= */

export const ORG_UPDATE = gql`
    mutation AppOrgUpdate(
        $id: ID!
        $businessName: String!
        $businessEmail: String
        $supportEmail: String
        $phoneNumber: String
        $websiteUrl: String
        $emergencyDeveloperEmail: String
        $emergencyDeveloperPhoneNumber: String!
        $termsApiAgreed: Boolean!
        $termsPartnerAgreed: Boolean!
    ) {
        appOrgUpdate(
            id: $id
            orgInfo: {
                businessName: $businessName
                businessEmail: $businessEmail
                supportEmail: $supportEmail
                phoneNumber: $phoneNumber
                websiteUrl: $websiteUrl
                emergencyDeveloperEmail: $emergencyDeveloperEmail
                emergencyDeveloperPhoneNumber: $emergencyDeveloperPhoneNumber
                termsApiAgreed: $termsApiAgreed
                termsPartnerAgreed: $termsPartnerAgreed
            }
        ) {
            node {
                id
                businessName
                businessEmail
                supportEmail
                phoneNumber
                websiteUrl
                emergencyDeveloperEmail
                emergencyDeveloperPhoneNumber
                termsApiAgreed
                termsPartnerAgreed
            }
        }
    }
`;

export const UPDATE_PAYOUT_SETTINGS = gql`
    mutation UpdatePayoutSettings($payoutSettings: PayoutSettingsInput!) {
        payoutSettingsUpdate(payoutSettings: $payoutSettings) {
            node {
                ...PayoutSettingsCommon
            }
            successful
        }
    }

    ${PayoutSettingsFragment}
`;

export const CREATE_PAYOUT_REQUEST = gql`
    mutation SubmitPayoutRequest($fileInput: AppFileInputInput!) {
        submitPayoutRequest(fileInput: $fileInput) {
            successful
            node {
                id
            }
        }
    }
`;

export const UPDATE_PAYOUT_REQUEST = gql`
    mutation UpdatePayoutRequest($id: ID!, $fileInput: AppFileInputInput!) {
        updatePayoutRequest(id: $id, fileInput: $fileInput) {
            successful
            node {
                id
            }
        }
    }
`;

export const INVOICE_FILE_DELETE = gql`
    mutation InvoiceFileDelete($fileKey: String!) {
        invoiceFileDelete(fileKey: $fileKey) {
            successful
        }
    }
`;

/* =================================================
        WEBHOOKS
   ================================================= */

export const WEBHOOK_CREATE = gql`
    mutation WebhookCreate($appId: ID!, $topic: AppWebhookTopic!, $url: String!) {
        webhookCreate(id: $appId, webhookInfo: { topic: $topic, url: $url }) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                webhookSubscriptions {
                    id
                    creationDate
                    topic
                    url
                }
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

export const WEBHOOK_UPDATE = gql`
    mutation WebhookUpdate($id: ID!, $topic: AppWebhookTopic!, $url: String!) {
        webhookUpdate(id: $id, webhookInfo: { topic: $topic, url: $url }) {
            node {
                id
                creationDate
                topic
                url
            }
        }
    }
`;

export const WEBHOOK_DELETE = gql`
    mutation WebhookDelete($id: ID!) {
        webhookDelete(id: $id) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                webhookSubscriptions {
                    id
                    creationDate
                    topic
                    url
                }
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

/* =================================================
        APPS
   ================================================= */

export const APP_CREATE = gql`
    mutation AppCreate(
        $name: String!
        $apiContactEmail: String!
        $appUrl: String
        $redirectUrlWhitelist: String
        $chargeType: AppChargeType!
        $type: AppVisibilityType!
        $installationCountries: [String]
        $oneTimeChargeAmount: Float
        $freeTrialDays: Int
        $appReviewInstructions: String
    ) {
        appCreate(
            appInfo: {
                name: $name
                apiContactEmail: $apiContactEmail
                appUrl: $appUrl
                redirectUrlWhitelist: $redirectUrlWhitelist
                chargeType: $chargeType
                type: $type
                installationCountries: $installationCountries
                oneTimeChargeAmount: $oneTimeChargeAmount
                freeTrialDays: $freeTrialDays
                appReviewInstructions: $appReviewInstructions
            }
        ) {
            node {
                id
                name
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
            }
        }
    }
`;

export const APP_DELETE = gql`
    mutation AppDelete($id: ID!) {
        appDelete(id: $id) {
            node {
                id
            }
        }
    }
`;

export const APP_UPDATE = gql`
    mutation AppUpdate(
        $id: ID!
        $name: String!
        $type: AppVisibilityType!
        $apiContactEmail: String!
        $appUrl: String
        $redirectUrlWhitelist: String
        $chargeType: AppChargeType!
        $installationCountries: [String]
        $oneTimeChargeAmount: Float
        $freeTrialDays: Int
        $appReviewInstructions: String
    ) {
        appUpdate(
            id: $id
            appInfo: {
                name: $name
                type: $type
                apiContactEmail: $apiContactEmail
                appUrl: $appUrl
                redirectUrlWhitelist: $redirectUrlWhitelist
                chargeType: $chargeType
                installationCountries: $installationCountries
                oneTimeChargeAmount: $oneTimeChargeAmount
                freeTrialDays: $freeTrialDays
                appReviewInstructions: $appReviewInstructions
            }
        ) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                linkedVendor {
                    id
                    name
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                webhookSubscriptions {
                    id
                    creationDate
                    topic
                    url
                }
            }
        }
    }
`;

export const APP_PUBLISH_CUSTOM_APP = gql`
    mutation AppCustomPublish($appId: ID!) {
        appCustomPublish(appId: $appId) {
            status
        }
    }
`;

export const APP_CUSTOM_LINK_TO_VENDOR = gql`
    mutation AppCustomLinkToVendor($appId: ID!, $vendorId: ID!) {
        appCustomLinkToVendor(appId: $appId, vendorId: $vendorId) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                linkedVendor {
                    id
                    name
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

export const APP_TEST_UNINSTALL = gql`
    mutation AppTestUninstall($id: ID!) {
        appUninstallTest(id: $id) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                testInstallation {
                    id
                    creationDate
                }
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

export const APP_SUBMIT_FOR_REVIEW = gql`
    mutation AppSubmitForReview($id: ID!) {
        appSubmitForReview(id: $id) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

/* =================================================
        LISTINGS
   ================================================= */

export const LISTING_CREATE = gql`
    mutation AppListingCreate(
        $appId: ID!
        $name: String!
        $tagLine: String!
        $description: String!
        $searchTerms: [String]
        $integrations: [String]
        $promoVideoUrl: String
        $includeKeyBenefitsImages: Boolean!
        $supportEmail: String!
        $supportPhone: String
        $websiteUrl: String
        $privacyPolicyUrl: String
        $termsOfServiceUrl: String
        $icon: AppImageUpdateInput
        $screenShots: [AppScreenShotUpdateInput]
        $keyBenefits: [AppKeyBenefitUpdateInput]
        $pricePlans: [AppPricePlanDetailsUpdateInput]
        $showContactDetails: Boolean!
        $offerDemoRequest: Boolean!
    ) {
        appListingCreate(
            id: $appId
            listingInfo: {
                name: $name
                tagLine: $tagLine
                description: $description
                searchTerms: $searchTerms
                integrations: $integrations
                promoVideoUrl: $promoVideoUrl
                includeKeyBenefitsImages: $includeKeyBenefitsImages
                supportEmail: $supportEmail
                supportPhone: $supportPhone
                websiteUrl: $websiteUrl
                privacyPolicyUrl: $privacyPolicyUrl
                termsOfServiceUrl: $termsOfServiceUrl
                icon: $icon
                screenShots: $screenShots
                keyBenefits: $keyBenefits
                pricePlans: $pricePlans
                showContactDetails: $showContactDetails
                offerDemoRequest: $offerDemoRequest
            }
        ) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    showContactDetails
                    offerDemoRequest
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

export const LISTING_UPDATE = gql`
    mutation AppListingUpdate(
        $id: ID!
        $name: String!
        $tagLine: String!
        $description: String!
        $searchTerms: [String]
        $integrations: [String]
        $promoVideoUrl: String
        $includeKeyBenefitsImages: Boolean!
        $supportEmail: String!
        $supportPhone: String
        $websiteUrl: String
        $privacyPolicyUrl: String
        $termsOfServiceUrl: String
        $icon: AppImageUpdateInput
        $screenShots: [AppScreenShotUpdateInput]
        $keyBenefits: [AppKeyBenefitUpdateInput]
        $pricePlans: [AppPricePlanDetailsUpdateInput]
        $showContactDetails: Boolean!
        $offerDemoRequest: Boolean!
    ) {
        appListingUpdate(
            id: $id
            listingInfo: {
                name: $name
                tagLine: $tagLine
                description: $description
                searchTerms: $searchTerms
                integrations: $integrations
                promoVideoUrl: $promoVideoUrl
                includeKeyBenefitsImages: $includeKeyBenefitsImages
                supportEmail: $supportEmail
                supportPhone: $supportPhone
                websiteUrl: $websiteUrl
                privacyPolicyUrl: $privacyPolicyUrl
                termsOfServiceUrl: $termsOfServiceUrl
                icon: $icon
                screenShots: $screenShots
                keyBenefits: $keyBenefits
                pricePlans: $pricePlans
                showContactDetails: $showContactDetails
                offerDemoRequest: $offerDemoRequest
            }
        ) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                webhookSubscriptions {
                    id
                    creationDate
                    topic
                    url
                }
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    showContactDetails
                    offerDemoRequest
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

export const LISTING_DELETE = gql`
    mutation AppListingDelete($id: ID!) {
        appListingDelete(id: $id) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

export const LISTING_IMAGE_DELETE = gql`
    mutation AppListingImageDelete($id: ID!, $imageType: AppListingImageType!, $imageKey: String!) {
        appListingImageDelete(id: $id, imageType: $imageType, imageKey: $imageKey) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
                listings {
                    id
                    creationDate
                    name
                    tagLine
                    description
                    searchTerms
                    integrations
                    promoVideoUrl
                    includeKeyBenefitsImages
                    supportEmail
                    supportPhone
                    websiteUrl
                    privacyPolicyUrl
                    termsOfServiceUrl
                    icon {
                        s3Key
                        fileName
                        fileType
                        height
                        width
                        size
                        sourceUrl
                    }
                    screenShots {
                        type
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    keyBenefits {
                        title
                        description
                        image {
                            s3Key
                            fileName
                            fileType
                            height
                            width
                            size
                            sourceUrl
                        }
                    }
                    pricePlans {
                        planId
                        name
                        features
                    }
                }
            }
        }
    }
`;

/* =================================================
        PRICE PLANS
   ================================================= */

export const PRICE_PLAN_CREATE = gql`
    mutation AppPricePlanCreate(
        $appId: ID!
        $name: String!
        $type: AppPricePlanEnumType!
        $discountedYearlyPrice: Boolean!
        $monthlyAmount: Float
        $yearlyAmount: Float
    ) {
        appPricePlanCreate(
            id: $appId
            planInfo: {
                name: $name
                type: $type
                discountedYearlyPrice: $discountedYearlyPrice
                monthlyAmount: $monthlyAmount
                yearlyAmount: $yearlyAmount
            }
        ) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
            }
        }
    }
`;

export const PRICE_PLAN_UPDATE = gql`
    mutation AppPricePlanUpdate(
        $id: ID!
        $name: String!
        $type: AppPricePlanEnumType!
        $discountedYearlyPrice: Boolean!
        $monthlyAmount: Float
        $yearlyAmount: Float
    ) {
        appPricePlanUpdate(
            id: $id
            planInfo: {
                name: $name
                type: $type
                discountedYearlyPrice: $discountedYearlyPrice
                monthlyAmount: $monthlyAmount
                yearlyAmount: $yearlyAmount
            }
        ) {
            node {
                id
                appId
                creationDate
                name
                type
                discountedYearlyPrice
                monthlyAmount
                yearlyAmount
            }
        }
    }
`;

export const PRICE_PLAN_DELETE = gql`
    mutation AppPricePlanDelete($id: ID!) {
        appPricePlanDelete(id: $id) {
            node {
                id
                name
                creationDate
                status
                apiContactEmail
                apiCredentials {
                    apiKey
                    secretKey
                }
                numberOfInstallations
                appUrl
                redirectUrlWhitelist
                chargeType
                type
                installationCountries
                oneTimeChargeAmount
                freeTrialDays
                appReviewInstructions
                pricePlans {
                    id
                    creationDate
                    name
                    type
                    discountedYearlyPrice
                    monthlyAmount
                    yearlyAmount
                }
            }
        }
    }
`;

/* =================================================
        USERS
   ================================================= */

export const USER_LOGOUT = gql`
    mutation AppDeveloperLogoutMutation {
        appAccountLogout
    }
`;

export const USER_LOGIN = gql`
    mutation AppDeveloperLoginMutation($email: String!, $password: String!) {
        appAccountLogin(account: { email: $email, password: $password }) {
            node {
                id
                email
                firstName
                lastName
                orgId
                orgName
                accountOwner
            }
        }
    }
`;

export const USER_SIGNUP = gql`
    mutation AppDeveloperSignupMutation(
        $email: String!
        $password: String!
        $firstName: String!
        $lastName: String!
        $termsApiAgreed: Boolean!
        $termsPartnerAgreed: Boolean!
    ) {
        appAccountSignup(
            account: {
                email: $email
                password: $password
                firstName: $firstName
                lastName: $lastName
                termsApiAgreed: $termsApiAgreed
                termsPartnerAgreed: $termsPartnerAgreed
            }
        ) {
            node {
                id
                email
                firstName
                lastName
                orgId
                orgName
            }
        }
    }
`;

export const USER_UPDATE = gql`
    mutation AppUserAccountUpdate(
        $id: ID!
        $firstName: String!
        $lastName: String!
        $password: String
    ) {
        appAccountUserUpdate(
            id: $id
            userInfo: { firstName: $firstName, lastName: $lastName, password: $password }
        ) {
            node {
                id
                email
                firstName
                lastName
                orgId
                orgName
            }
        }
    }
`;

export const USER_DELETE = gql`
    mutation AppUserAccountDelete($id: ID!) {
        appAccountUserDelete(id: $id) {
            node {
                id
            }
        }
    }
`;

export const USER_CREATE = gql`
    mutation AppUserAccountCreate(
        $firstName: String!
        $lastName: String!
        $email: String!
        $password: String!
    ) {
        appAccountUserCreate(
            userInfo: {
                firstName: $firstName
                lastName: $lastName
                email: $email
                password: $password
            }
        ) {
            node {
                id
                email
                firstName
                lastName
                orgId
                orgName
            }
        }
    }
`;

export const USER_CHANGE_ORG = gql`
    mutation AppUserAccountChangeOrg($id: ID!) {
        appAccountUserChangeOrg(id: $id) {
            node {
                id
                email
                firstName
                lastName
                orgId
                orgName
                adminUser
                organizationId
            }
        }
    }
`;
